import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import quizApiService from '../../services/quizApi';
import logger from '../../utils/logging/logger';
import { studentNavigationItems, navigationItems } from './sidebarNavigationItems';
import { calculatePendingTasks, calculateCompletedTasks } from './sidebarTaskCounters';

export default function useSidebar(onClose) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    courses: 0,
    pending: 0,
    completed: 0
  });

  // Search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (user && user.role === 'student') {
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Search functionality
  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    logger.debug('Searching for', { query });
    setIsSearching(true);
    try {
      const results = [];

      // Use dedicated search API endpoint
      try {
        logger.debug('Using search API...');
        const searchResponse = await apiService.search(query, 'all');

        if (searchResponse && searchResponse.success !== false) {
          // Handle search API response - structure: { success: true, results: { courses: [], assignments: [], ... } }
          const searchResults = searchResponse.results || searchResponse.data || searchResponse;

          // Process courses
          if (searchResults.courses && Array.isArray(searchResults.courses)) {
            searchResults.courses.forEach(course => {
              results.push({
                id: course.id,
                title: course.name || course.title,
                subtitle: `Course${course.code ? ` - ${course.code}` : ''}`,
                type: 'course',
                path: `/courses/${course.id}`
              });
            });
          }

          // Process assignments
          if (searchResults.assignments && Array.isArray(searchResults.assignments)) {
            searchResults.assignments.forEach(assignment => {
              results.push({
                id: assignment.id,
                title: assignment.title,
                subtitle: `Assignment${assignment.course_name ? ` - ${assignment.course_name}` : ''}`,
                type: 'assignment',
                path: user?.role === 'student'
                  ? `/assignments?assignmentId=${encodeURIComponent(assignment.id)}`
                  : `/assignments/${assignment.id}`
              });
            });
          }

          // Process quizzes/exams
          if (searchResults.quizzes && Array.isArray(searchResults.quizzes)) {
            searchResults.quizzes.forEach(quiz => {
              const quizId = quiz.assignment_id || quiz.quiz_id || quiz.id;
              let path = '/student/quizzes';

              if (user?.role === 'student') {
                if (quiz.submitted_at && quiz.latest_attempt_id) {
                  path = `/student/result/${quiz.latest_attempt_id}`;
                } else if (quiz.latest_attempt_id) {
                  path = `/student/attempt/${quiz.latest_attempt_id}`;
                } else if (quiz.assignment_id) {
                  path = `/student/assignments/${quiz.assignment_id}/warning`;
                }
              } else if (quiz.quiz_id || quiz.id) {
                path = `/instructor/quizzes`;
              }

              results.push({
                id: quizId,
                title: quiz.title,
                subtitle: `Quiz/Exam${quiz.course_name ? ` - ${quiz.course_name}` : ''}`,
                type: 'quiz',
                path
              });
            });
          }

          // Process notes
          if (searchResults.notes && Array.isArray(searchResults.notes)) {
            searchResults.notes.forEach(note => {
              results.push({
                id: note.id,
                title: note.title || 'Untitled note',
                subtitle: `Note${note.course_name ? ` - ${note.course_name}` : ''}${note.week_number ? ` - Week ${note.week_number}` : ''}`,
                type: 'note',
                path: `/student/notes/${encodeURIComponent(note.id)}`
              });
            });
          }

          if (user?.role === 'student' && !results.some(result => result.type === 'quiz')) {
            try {
              const quizResponse = await apiService.request('/quizzes/student/assignments?limit=100');
              const quizAssignments = quizResponse?.data || quizResponse?.assignments || [];
              quizAssignments
                .filter(quiz => {
                  const normalizedQuery = query.toLowerCase();
                  return [quiz.title, quiz.description, quiz.course_name, quiz.course_code]
                    .filter(Boolean)
                    .some(value => String(value).toLowerCase().includes(normalizedQuery));
                })
                .slice(0, 10)
                .forEach(quiz => {
                  let path = '/student/quizzes';
                  if (quiz.submitted_at && quiz.latest_attempt_id) {
                    path = `/student/result/${quiz.latest_attempt_id}`;
                  } else if (quiz.latest_attempt_id) {
                    path = `/student/attempt/${quiz.latest_attempt_id}`;
                  } else if (quiz.assignment_id) {
                    path = `/student/assignments/${quiz.assignment_id}/warning`;
                  }

                  results.push({
                    id: quiz.assignment_id || quiz.quiz_id,
                    title: quiz.title,
                    subtitle: `Quiz/Exam${quiz.course_name ? ` - ${quiz.course_name}` : ''}`,
                    type: 'quiz',
                    path
                  });
                });
            } catch (err) {
              logger.warn('Error in supplemental quiz search', { err });
            }
          }

          // Process events if available
          if (searchResults.events && Array.isArray(searchResults.events)) {
            searchResults.events.forEach(event => {
              results.push({
                id: event.id,
                title: event.title,
                subtitle: `Event${event.course_name ? ` - ${event.course_name}` : ''}`,
                type: 'event',
                path: `/calendar`
              });
            });
          }
        }
      } catch (error) {
        logger.warn('Search API error, falling back to client-side search', { error, query });

        // Fallback: client-side search if API fails
        try {
          const coursesResponse = await apiService.get('/courses');
          if (coursesResponse && coursesResponse.data) {
            const courses = Array.isArray(coursesResponse.data) ? coursesResponse.data : [];
            courses.filter(course =>
              (course.title || course.name) && (
                (course.title || course.name).toLowerCase().includes(query.toLowerCase()) ||
                (course.description && course.description.toLowerCase().includes(query.toLowerCase()))
              )
            ).forEach(course => {
              results.push({
                id: course.id,
                title: course.title || course.name,
                subtitle: 'Course',
                type: 'course',
                path: `/courses/${course.id}`
              });
            });
          }
        } catch (err) {
          logger.warn('Error in fallback course search', { err });
        }

        try {
          const assignmentsResponse = await apiService.getAssignments();
          if (assignmentsResponse && assignmentsResponse.success && assignmentsResponse.assignments) {
            assignmentsResponse.assignments.filter(assignment =>
              assignment.title && (
                assignment.title.toLowerCase().includes(query.toLowerCase()) ||
                (assignment.description && assignment.description.toLowerCase().includes(query.toLowerCase()))
              )
            ).forEach(assignment => {
              results.push({
                id: assignment.id,
                title: assignment.title,
                subtitle: `Assignment - ${assignment.course_name || 'Course'}`,
                type: 'assignment',
                path: `/assignments/${assignment.id}`
              });
            });
          }
        } catch (err) {
          logger.warn('Error in fallback assignment search', { err });
        }

        if (user?.role === 'student') {
          try {
            const quizResponse = await apiService.request('/quizzes/student/assignments?limit=100');
            const normalizedQuery = query.toLowerCase();
            const quizAssignments = quizResponse?.data || quizResponse?.assignments || [];
            quizAssignments
              .filter(quiz => [quiz.title, quiz.description, quiz.course_name, quiz.course_code]
                .filter(Boolean)
                .some(value => String(value).toLowerCase().includes(normalizedQuery)))
              .slice(0, 10)
              .forEach(quiz => {
                let path = '/student/quizzes';
                if (quiz.submitted_at && quiz.latest_attempt_id) {
                  path = `/student/result/${quiz.latest_attempt_id}`;
                } else if (quiz.latest_attempt_id) {
                  path = `/student/attempt/${quiz.latest_attempt_id}`;
                } else if (quiz.assignment_id) {
                  path = `/student/assignments/${quiz.assignment_id}/warning`;
                }

                results.push({
                  id: quiz.assignment_id || quiz.quiz_id,
                  title: quiz.title,
                  subtitle: `Quiz/Exam${quiz.course_name ? ` - ${quiz.course_name}` : ''}`,
                  type: 'quiz',
                  path
                });
              });
          } catch (err) {
            logger.warn('Error in fallback quiz search', { err });
          }

          try {
            const notesResponse = await apiService.request(`/notes/mine?search=${encodeURIComponent(query)}&pageSize=10`);
            const notes = notesResponse?.notes || notesResponse?.data?.notes || [];
            notes.forEach(note => {
              results.push({
                id: note.id,
                title: note.title || 'Untitled note',
                subtitle: `Note${note.course_name ? ` - ${note.course_name}` : ''}`,
                type: 'note',
                path: `/student/notes/${encodeURIComponent(note.id)}`
              });
            });
          } catch (err) {
            logger.warn('Error in fallback notes search', { err });
          }
        }
      }

      // Fallback: search navigation items if no results
      if (results.length === 0) {
        logger.debug('No API results, searching navigation items...');
        const navSource = user?.role === 'student' ? studentNavigationItems : navigationItems;
        navSource.forEach(section => {
          section.items.forEach(item => {
            if (item.label && item.label.toLowerCase().includes(query.toLowerCase())) {
              results.push({
                id: item.path,
                title: item.label,
                subtitle: section.section,
                type: 'navigation',
                path: item.path
              });
            }
          });
        });
      }

      logger.debug('Final search results', { count: results.length });
      setSearchResults(results.slice(0, 20));
      setShowSearchResults(true);
    } catch (error) {
      logger.error('Search error', { error, query });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchResultClick = (result) => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
    onClose(); // Close sidebar on mobile
    navigate(result.path);
  };

  const handleSearchBlur = () => {
    // Delay hiding results to allow clicking on them
    setTimeout(() => {
      setShowSearchResults(false);
    }, 200);
  };

  const fetchStats = async () => {
    try {
      // Fetch same data as Dashboard for accurate calculations
      const [statsResponse, assignmentsResponse, quizzesResponse] = await Promise.allSettled([
        apiService.getDashboardStats(),
        apiService.getAssignments(),
        quizApiService.getStudentAssignments()
      ]);

      // Extract active courses count from stats
      let activeCourses = 0;
      if (statsResponse.status === 'fulfilled' && statsResponse.value?.success && statsResponse.value?.stats) {
        activeCourses = statsResponse.value.stats.totalCourses || 0;
      }

      // Get assignments from active courses
      let allActiveAssignments = [];
      if (assignmentsResponse.status === 'fulfilled' && assignmentsResponse.value?.success && assignmentsResponse.value?.assignments) {
        allActiveAssignments = assignmentsResponse.value.assignments.filter(
          assignment => assignment.course_is_active === 1 || assignment.course_is_active === true
        );
      }

      // Get quizzes
      let quizzes = [];
      if (quizzesResponse.status === 'fulfilled' && quizzesResponse.value?.success && quizzesResponse.value?.data) {
        quizzes = quizzesResponse.value.data;
      }

      // Calculate pending and completed tasks using same logic as Dashboard
      const pendingTasks = calculatePendingTasks(allActiveAssignments, quizzes);
      const completedTasks = calculateCompletedTasks(allActiveAssignments, quizzes);

      setStats({
        courses: activeCourses,
        pending: pendingTasks,
        completed: completedTasks
      });
    } catch (error) {
      logger.error('Error fetching stats', { error });
      setStats({
        courses: 0,
        pending: 0,
        completed: 0
      });
    }
  };

  return {
    user,
    stats,
    searchTerm,
    searchResults,
    showSearchResults, setShowSearchResults,
    isSearching,
    handleSearchChange,
    handleSearchResultClick,
    handleSearchBlur,
  };
}
